import { Table as ComponentsTable, EmptyState } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Row } from "react-table";
import ReactTooltip from "react-tooltip";

import { createColumns } from "./TenantsTableColumns";
import { ViewTenantDetails } from "components/customer-details/ViewTenantDetails";
import Chevron from "components/icons/Chevron";
import { subpageContext, viewActionsContext } from "components/layout/ApplicationLayout";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { allowDelivery } from "components/licenses/delivery-history/AddLicenseDeliveryView";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import SearchView from "components/search/SearchView";
import Table from "components/table/Table";
import tableStyle from "components/table/table.scss";
import {
    clearTenantTableFiltersRestore,
    DEFAULT_TENANT_TABLE_FILTERS,
    readTenantTableFiltersToRestore,
    saveTenantTableFilters,
} from "components/tenants/tenants-table/tenantTableFilters";
import style from "components/tenants/tenants.scss";
import { AUTH_TENANT_ACCESS } from "domain/authority";
import { CombinedTier, LicensingModel, Tenant, TenantCursor, Tenants, TenantType } from "domain/tenants";
import { FLAG_UICOMPS_TENANTS_TABLE } from "services/feature/FeatureFlagService";
import { ProductToRateList } from "services/licenses/LicenseService";
import { tenantService } from "services/tenants/TenantService";
import { userSessionService } from "services/user/UserSessionService";
import { store } from "store";
import buttonStyle from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { RepositoryKey } from "utils/repository";

import testIds from "testIds.json";

interface Props {
    scrollPosition?: number;
    count: number;
    refreshCount: number;
    onTenantEdit: () => void;
    onTenantDelete: () => void;
    initialTenantData?: Tenants;
    parentLevel: number;
    setNewComponent: (value: boolean) => void;
    tenantUuid: (value: string) => void;
    tenantName: (value: string) => void;
    rateVersions?: ProductToRateList[];
}

export interface TableState {
    tenants: Tenant[];
    cursor: TenantCursor | null;
    scrollPosition?: number;
}

export const TenantsTable = (rawProps: Props) => {
    const props = { initialTenantData: { tenantList: [], cursor: null }, ...rawProps };
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const rowRefs = React.useRef<Array<React.RefObject<HTMLTableRowElement>>>([]);
    const context = React.useContext(subpageContext);
    const fetchTenantInfoAllowed =
        userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
        userSessionService.userHasAllAuthorities([AUTH_TENANT_ACCESS]);
    //TODO BCC-7002 isTenantsTableFeatureEnabled can be removed after Tenants table rewrite is done
    const isTenantsTableFeatureEnabled = useFeature(FLAG_UICOMPS_TENANTS_TABLE) as boolean;
    const viewActions = React.useContext(viewActionsContext);

    const [loading, setLoading] = React.useState<boolean>(false);
    const [initialFilters] = React.useState(() => readTenantTableFiltersToRestore() ?? DEFAULT_TENANT_TABLE_FILTERS);
    const [searchQuery, setSearchQuery] = React.useState(initialFilters.searchQuery);
    const [selectedSearchParameter, setSelectedSearchParameter] = React.useState(initialFilters.searchParameter);
    const [selectedContractTypeFilter, setSelectedContractTypeFilter] = React.useState(initialFilters.contractType);
    const [state, setState] = React.useState<TableState>({
        tenants: props.initialTenantData.tenantList,
        cursor: props.initialTenantData.cursor,
        scrollPosition: props.scrollPosition,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);

    const showTenantProfile = (tenant: Tenant) => {
        context?.setSubpage({
            content: (
                <SubpageLayout
                    visible={true}
                    title={t("CustomerProfile.customerName", { customerName: tenant.name })}
                    primaryButtonRequired={true}
                >
                    {<ViewTenantDetails activeTenant={tenant} />}
                </SubpageLayout>
            ),
            isModal: true,
        });
    };

    //TODO BCC-7002 Remove function as redundant after Tenants table rewrite is done
    const createDropdownArrow = (isExpanded: boolean) => {
        const theme = store.getState();

        return (
            <Chevron
                color={theme.themeReducer.theme.iconFillColor}
                className={classNames(style.toggleArrow, style.right, { [style.down]: isExpanded })}
                width={20}
                height={20}
            />
        );
    };

    //TODO BCC-7002 Remove function as redundant after Tenants table rewrite is done
    const showConnectorLines = (depth: number) => {
        return (
            <div className={tableStyle.nameCellConnectorLines}>
                <span></span>
                {depth >= 2 && <span></span>}
                {depth >= 3 && <span></span>}
            </div>
        );
    };

    const highlightText = (text: string, highlight: string) => {
        const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const partToHighlight = new RegExp(`(${escapedHighlight})`, "i");
        const fullText = text.split(partToHighlight);

        return (
            <div>
                {fullText.map((word, index) =>
                    word.toLowerCase() === highlight.toLowerCase() ? <mark key={index}>{word}</mark> : word
                )}
            </div>
        );
    };

    const addSubTenants = (tenantList: Tenant[], data: Tenant[], row: Row<Tenant>): Tenant[] => {
        return addSubTenantsToTenants(tenantList, data, row, row.id.split("."));
    };

    const addSubTenantsToTenants = (
        tenantList: Tenant[],
        data: Tenant[],
        row: Row<Tenant>,
        rowId: string[]
    ): Tenant[] => {
        const tenantId = Number(rowId[0]);

        if (rowId.length === 1) {
            return tenantList.map((tenant, index) => {
                if (index !== tenantId) {
                    return tenant;
                }

                return {
                    ...tenant,
                    tenants: data,
                };
            });
        }

        return tenantList.map((tenant, index) => {
            if (index !== tenantId) {
                return tenant;
            }

            if (!tenant.tenants) {
                return tenant;
            }

            const pathToSubTenant = rowId.slice(1);

            return {
                ...tenant,
                tenants: addSubTenantsToTenants(tenant.tenants, data, row, pathToSubTenant),
            };
        });
    };

    const createPlaceholderTableRows = (row: Row<Tenant>) => {
        const placeholderObjects = Array(row.original.subTenantsCount).fill({});

        setState((prevState) => ({
            ...prevState,
            tenants: addSubTenants(prevState.tenants, placeholderObjects, row),
        }));
    };

    const fetchSubTenants = (row: Row<Tenant>) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        tenantService
            .fetchSubTenants(row.original.uuid, abortController)
            .then((data) => {
                const currentDepth = row.original.depth ?? 1;
                const tenantsWithDepth = addDepthToTenants(data.tenantList.tenantList, currentDepth + 1);
                setState((prevState) => ({
                    ...prevState,
                    tenants: addSubTenants(prevState.tenants, tenantsWithDepth, row),
                }));
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("CustomersTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setInitialLoading(false);
                }
            });
    };

    const createSelectedTenantHierarchy = (rows: Row<Tenant>[], selectedRowId: string): Row<Tenant>[] => {
        const computedRows: Row<Tenant>[] = [];
        const selectedRowDepth = selectedRowId.split(".");
        let reconstructedId = "";
        for (const idPart of selectedRowDepth) {
            if (reconstructedId.length === 0) {
                reconstructedId = idPart;
            } else {
                reconstructedId = reconstructedId + "." + idPart;
                if (reconstructedId === selectedRowId) {
                    break;
                }
            }
            for (const eachRow of rows) {
                if (reconstructedId === eachRow.id) {
                    computedRows.push(eachRow);
                    break;
                }
            }
        }
        return computedRows;
    };

    function showDelivery(
        licensingModel: LicensingModel,
        parentModel: LicensingModel,
        parentType: TenantType,
        parentTier: CombinedTier,
        tenantTier: CombinedTier
    ) {
        return allowDelivery(parentType, parentModel, licensingModel, parentTier, tenantTier);
    }

    function showCommonLicenseCount(licensingModel: string, commonLicenseCount?: number) {
        if (licensingModel !== LicensingModel.BUNDLE_WITH_TOKEN || typeof commonLicenseCount === "undefined") {
            return t("Common.na");
        }
        return commonLicenseCount;
    }

    const columns = createColumns(
        rowRefs,
        fetchTenantInfoAllowed,
        showTenantProfile,
        createDropdownArrow,
        showConnectorLines,
        highlightText,
        createPlaceholderTableRows,
        fetchSubTenants,
        createSelectedTenantHierarchy,
        showDelivery,
        showCommonLicenseCount,
        tableContainerRef,
        props.onTenantEdit,
        props.onTenantDelete,
        props.parentLevel,
        props.setNewComponent,
        props.tenantUuid,
        props.tenantName,
        props.rateVersions,
        state,
        searchQuery,
        isTenantsTableFeatureEnabled
    );

    const fetchData = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);

        tenantService
            .fetchTenants(
                abortController,
                initialLoading ? null : state.cursor,
                selectedSearchParameter === "search" ? searchQuery : "",
                selectedSearchParameter === "username" ? searchQuery : "",
                undefined,
                undefined,
                selectedContractTypeFilter
            )
            .then((data) => {
                setState((prevState) => ({
                    ...prevState,
                    tenants: prevState.tenants.concat(data.tenantList.tenantList),
                    scrollPosition: prevState.tenants.length - 1,
                    cursor: data.tenantList.cursor,
                }));
                setLoading(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("CustomersTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setInitialLoading(false);
                }
            });
    };

    React.useEffect(() => {
        setState({
            tenants: [],
            cursor: null,
            scrollPosition: 0,
        });
        if (props.initialTenantData.tenantList.length > 0) {
            return;
        }

        fetchData(true);
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [props.count, searchQuery, selectedSearchParameter, props.refreshCount, selectedContractTypeFilter]);

    React.useEffect(() => {
        clearTenantTableFiltersRestore();
    }, []);

    React.useEffect(() => {
        saveTenantTableFilters({
            contractType: selectedContractTypeFilter,
            searchParameter: selectedSearchParameter,
            searchQuery,
        });
    }, [selectedContractTypeFilter, selectedSearchParameter, searchQuery]);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [state.tenants]);

    let dataCount = null;
    if (state.tenants.length > 0) {
        dataCount = t("Common.defaultSearchResultHint", { dataCount: state.tenants.length });
    }

    const emptyStateMessage = searchQuery
        ? t("Common.searchEmptyStateMessage", { searchQuery: searchQuery })
        : t("CustomersTable.emptyStateMessage");

    function generateContractTypeOptions(): string[] {
        return ["all", "trial", "production"];
    }

    const contractTypeToTranslationKeyMap = new Map<string, string>([
        ["all", t("CustomersTable.allUsecaseType")],
        ["trial", t("AddCustomerForm.contractType.trial")],
        ["production", t("AddCustomerForm.contractType.production")],
    ]);

    return (
        <>
            <div className={layoutStyle.aboveTable}>
                <div className={layoutStyle.recordCount}>{dataCount}</div>

                <div className={style.filter}>
                    {
                        <>
                            <div className={classNames(formStyle.search, style.marginRight16Px)}>
                                <select
                                    id={"contractTypeSelector"}
                                    value={selectedContractTypeFilter}
                                    onChange={(event) => setSelectedContractTypeFilter(event.target.value)}
                                    className={classNames(formStyle.select, formStyle.fixedWidthInput)}
                                    data-testid={testIds.workArea.tenant.usecaseTypeSelect}
                                >
                                    {generateContractTypeOptions().map((value, index) => (
                                        <option key={index} value={value}>
                                            {contractTypeToTranslationKeyMap.get(value)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={classNames(formStyle.search, style.marginRight4Px)}>
                                <SearchView
                                    key="tenantDetails"
                                    setSearch={setSearchQuery}
                                    initialSearchQuery={initialFilters.searchQuery}
                                    searchInProgress={loading}
                                    placeholder={t("Common.searchBy")}
                                />
                            </div>
                            <div className={formStyle.search}>
                                <select
                                    id={"searchParameterSelector"}
                                    value={selectedSearchParameter}
                                    onChange={(event) => setSelectedSearchParameter(event.target.value)}
                                    className={classNames(formStyle.select, formStyle.fixedWidthInput)}
                                    data-testid={testIds.workArea.tenant.searchSelect}
                                >
                                    <option key={"tenantsDetails"} value={"search"}>
                                        {t("CustomersTable.tenantDetails")}
                                    </option>
                                    <option key={"users"} value={"username"}>
                                        {t("Common.users")}
                                    </option>
                                </select>
                            </div>
                        </>
                    }
                </div>
            </div>
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                {isTenantsTableFeatureEnabled ? (
                    <div className={layoutStyle.tableInnerWrapper}>
                        <ComponentsTable
                            tableIdentity={RepositoryKey.TENANT_TABLE}
                            data={state.tenants.sort((a, b) => a.name.localeCompare(b.name))}
                            columns={columns}
                            loaded={!initialLoading}
                            emptyStateMessage={<EmptyState text={emptyStateMessage} viewActions={viewActions} />}
                            testIds={{ itself: testIds.workArea.tenant.table.itself }}
                            failureMessage={requestFailureMessage}
                            isRowDisabled={(tenant) => tenant.deletionStatus}
                            getSubRows={(tenant) => tenant.tenants}
                            containerRef={tableContainerRef}
                            scrollTo={state.scrollPosition}
                        />
                    </div>
                ) : (
                    //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
                    <Table
                        tableIdentity={RepositoryKey.TENANT_TABLE}
                        data={state.tenants.sort((a, b) => a.name.localeCompare(b.name))}
                        columns={columns}
                        loaded={!initialLoading}
                        failureMessage={requestFailureMessage}
                        tooltips={true}
                        emptyMessage={emptyStateMessage}
                        testId={testIds.workArea.tenant.table.itself}
                        noAction={searchQuery !== ""}
                    />
                )}
            </div>
            {state.cursor != null &&
                state.tenants.length >= 100 &&
                state.tenants.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
        </>
    );
};

function addDepthToTenants(tenants: Tenant[], depth: number): Tenant[] {
    return tenants.map((tenant) => ({
        ...tenant,
        depth,
    }));
}
